import { clsx, type ClassValue } from "clsx";
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { twMerge } from "tailwind-merge";

dayjs.extend(utc);
dayjs.extend(quarterOfYear);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Mock API handler for Total Inbound Calls
export function mockFetchInboundCalls() {
  return Promise.resolve({
    current: 1770,
    previous: 1632,
    changePercent: 8.4
  });
}

export function mockFetchOutboundCalls() {
  return Promise.resolve({
    current: 1200,
    previous: 1100,
    changePercent: 9.1
  });
}

export function mockFetchInternalCalls() {
  return Promise.resolve({
    current: 500,
    previous: 520,
    changePercent: -3.8
  });
}

export function mockFetchAbandonedCalls() {
  return Promise.resolve({
    current: 100,
    previous: 80,
    changePercent: 25.0
  });
}

export function mockFetchForwardedCalls() {
  return Promise.resolve({
    current: 200,
    previous: 180,
    changePercent: 11.1
  });
}

export function mockFetchAbandonedRate() {
  return Promise.resolve({
    current: 5.6,
    previous: 4.2,
    changePercent: 33.3
  });
}

export function mockFetchCallVolumeTotal() {
  return Promise.resolve({
    current: 2970,
    previous: 2732,
    changePercent: 8.7
  });
}

export function mockFetchCallsHandledByQueue() {
  return Promise.resolve({
    current: 800,
    previous: 750,
    changePercent: 6.7
  });
}

export function mockFetchAvgWaitingTime() {
  return Promise.resolve({
    current: '1:45',
    previous: '2:00',
    changePercent: -12.5
  });
}

export function mockFetchAvgHandlingTime() {
  return Promise.resolve({
    current: '3:15',
    previous: '3:30',
    changePercent: -7.1
  });
}

export function mockFetchAvgSpeedOfAnswer() {
  return Promise.resolve({
    current: '0:30',
    previous: '0:40',
    changePercent: -25.0
  });
}

export function mockFetchCallVolumeChart() {
  return Promise.resolve({
    data: [
      { week: 'Week 1', inbound: 400, outbound: 250 },
      { week: 'Week 2', inbound: 370, outbound: 200 },
      { week: 'Week 3', inbound: 450, outbound: 270 },
      { week: 'Week 4', inbound: 490, outbound: 300 },
    ]
  });
}

export function mockFetchCallDetailTable() {
  return Promise.resolve([
    {
      id: '1',
      startTime: '2025-04-24 09:58:13',
      phoneNumber: '+12522485816',
      direction: 'Inbound',
      callStatus: 'Answered',
      callQueue: 'US - CQ - Demo - Sales',
    },
    {
      id: '2',
      startTime: '2025-04-24 09:58:18',
      phoneNumber: '+84906231990',
      direction: 'Inbound',
      callStatus: 'Answered',
      callQueue: 'US - CQ - Demo - Sales',
    },
  ]);
}

export function mockFetchCallJourney(callId: string) {
  // mock timeline for a call
  return Promise.resolve({
    customer: 'John Doe',
    agent: 'Alex Johnson',
    phone: '+1 (555) 123-4567',
    queue: 'Support',
    duration: '4:32',
    dateTime: '2023-05-15 10:45 AM',
    journey: [
      { time: '9:15 AM', label: 'Call initiated', icon: 'phone' },
      { time: '9:15 AM', label: 'Queued for 45 seconds', icon: 'clock' },
      { time: '9:16 AM', label: 'Connected to Alex Johnson', icon: 'user' },
      { time: '9:20 AM', label: 'Call completed', icon: 'phone-off' },
    ]
  });
}

export function mockFetchInboundStatusDistribution() {
  return Promise.resolve([
    { name: 'Answered', value: 75, color: '#23407a' },
    { name: 'Missed', value: 25, color: '#6ed6e9' },
  ]);
}

export function mockFetchOutboundStatusDistribution() {
  return Promise.resolve([
    { name: 'Answered', value: 1650, percent: 93, color: '#23407a' },
    { name: 'Missed', value: 120, percent: 7, color: '#6ed6e9' },
  ]);
}

export function mockFetchCallsHandledByQueuePie() {
  return Promise.resolve([
    { name: 'Technical Support', value: 450, percent: 26, color: '#23407a' },
    { name: 'Customer Service', value: 680, percent: 39, color: '#6ed6e9' },
    { name: 'Billing', value: 320, percent: 18, color: '#7ed6e9' },
    { name: 'Sales', value: 280, percent: 16, color: '#22307a' },
  ]);
}

export function formatSeconds(seconds: number): string {
  const totalSeconds = Math.round(seconds);

  // Check if >= 1 day (86400 seconds)
  if (totalSeconds >= 86400) {
    const days = Math.floor(totalSeconds / 86400);
    const remainingSecondsAfterDays = totalSeconds % 86400;
    const hours = Math.floor(remainingSecondsAfterDays / 3600);
    const minutes = Math.floor((remainingSecondsAfterDays % 3600) / 60);
    const remainingSeconds = remainingSecondsAfterDays % 60;

    return `${days}d, ${[hours, minutes, remainingSeconds]
      .map(val => val.toString().padStart(2, '0'))
      .join(':')}`;
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  return [hours, minutes, remainingSeconds]
    .map(val => val.toString().padStart(2, '0'))
    .join(':');
}

type TrendRangeType = 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export function formatXAxisLabel(label: string, rangeType: TrendRangeType) {
  const date = new Date(label);
  switch (rangeType) {
    case 'weekly':
      // Display day of week: Sun, Mon, Tue, Wed, Thu, Fri, Sat
      return date.toLocaleString('en-US', { weekday: 'short' });

    case 'monthly':
      // Display MM/dd format
      return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;

    case 'quarterly':
      // Display month name in quarter: April, May, June
      return date.toLocaleString('en-US', { month: 'short' });

    case 'yearly':
      // Display month name in year: Jan, Feb, Mar, ...
      return date.toLocaleString('en-US', { month: 'short' });

    default:
      return label;
  }
}

export function formatTrendsXAxisLabel(label: string, rangeType: 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'hour' | 'day' | 'year' | 'date-time') {
  // Convert UTC date to local timezone for formatting
  const date = dayjs.utc(label).local();
  if (!date.isValid()) return label;

  switch (rangeType) {
    case 'weekly':
      // Match the backend's TrendPeriodBuilder label format ("Week27 2026") — ISO week/week-year,
      // not dayjs's locale week().
      return `Week${String(date.isoWeek()).padStart(2, '0')} ${date.isoWeekYear()}`;
    case 'monthly':
      return date.format('MMM YYYY');
    case 'quarterly': {
      // Match the backend's TrendPeriodBuilder label format ("Quarter3 2026").
      const quarter = date.quarter();
      return `Quarter${quarter} ${date.year()}`;
    }
    case 'yearly':
      return date.format('YYYY');
    case 'hour':
      return date.format('HH:mm');
    case 'day':
      return date.format('MM/DD');
    case 'year':
      return date.format('MM/DD/YY');
    case 'date-time':
      return date.format('ddd, MMM D, hh:mm A');
    default:
      return label;
  }
}

// Build placeholder time buckets (no metric values) spanning dateRange so trend charts
// still render x/y axes instead of a "no data" message when the API returns nothing.
export function generateTrendPlaceholderBuckets(
  rangeType: TrendRangeType,
  dateRange: { from: Date; to: Date }
): { time: string }[] {
  const buckets: { time: string }[] = [];
  const end = dayjs(dateRange.to).startOf('day');

  if (rangeType === 'quarterly' || rangeType === 'yearly') {
    let cursor = dayjs(dateRange.from).startOf('month');
    const monthEnd = end.startOf('month');
    while (cursor.isSame(monthEnd) || cursor.isBefore(monthEnd)) {
      buckets.push({ time: cursor.toISOString() });
      cursor = cursor.add(1, 'month');
    }
    return buckets;
  }

  let cursor = dayjs(dateRange.from).startOf('day');
  while (cursor.isSame(end) || cursor.isBefore(end)) {
    buckets.push({ time: cursor.toISOString() });
    cursor = cursor.add(1, 'day');
  }
  return buckets;
}

function getGroupingKey(date: string, rangeType: TrendRangeType): string {
  // Convert UTC date to local timezone
  const d = dayjs.utc(date).local();
  switch (rangeType) {
    case 'weekly': {
      // Use ISO week and ISO week year for correct grouping
      return `${d.year()}-W${d.week()}`;
    }
    case 'monthly': {
      return d.format('YYYY-MM');
    }
    case 'quarterly': {
      return `${d.year()}-Q${d.quarter()}`;
    }
    case 'yearly': {
      return d.format('YYYY');
    }
    default:
      return d.toISOString();
  }
}

// Group data based on range type
export function groupDataByRangeType<T extends { time: string }>(
  data: T[],
  rangeType: TrendRangeType,
  aggregationFields: (keyof T)[],
  sumOnlyFields: (keyof T)[] = []
): T[] {
  if (!Array.isArray(data) || data.length === 0) return data;

  // Group data
  const groupedData = new Map<string, T[]>();

  data.forEach(item => {
    const groupingKey = getGroupingKey(item.time, rangeType);

    if (!groupedData.has(groupingKey)) {
      groupedData.set(groupingKey, []);
    }
    groupedData.get(groupingKey)!.push(item);
  });

  // Aggregate data for each group
  const aggregatedData: T[] = [];

  groupedData.forEach((groupItems, groupingKey) => {
    if (groupItems.length === 0) return;

    // Use the first item as base
    const baseItem = { ...groupItems[0] };

    // Aggregate numeric fields
    aggregationFields.forEach(field => {
      if (typeof baseItem[field] === 'number') {
        const sum = groupItems.reduce((acc, item) => acc + (item[field] as number), 0);

        // For quarterly rangeType, always sum the values
        if (rangeType === 'quarterly' || sumOnlyFields.includes(field)) {
          (baseItem as Record<keyof T, unknown>)[field] = Math.round(sum * 100) / 100;
        } else {
          const average = sum / groupItems.length;
          (baseItem as Record<keyof T, unknown>)[field] = Math.round(average * 100) / 100; // Round to 2 decimal places
        }
      }
    });

    // Set the groupKey for chart label, keep time for tooltip/detail
    (baseItem as Record<string, unknown>)["groupKey"] = groupingKey;
    // Set time to the earliest time in the group for correct XAxis labeling
    baseItem.time = groupItems.map(i => i.time).sort()[0];

    aggregatedData.push(baseItem);
  });

  // Sort by time
  return aggregatedData.sort((a, b) => dayjs(a.time).valueOf() - dayjs(b.time).valueOf());
}

export function getUnifiedChartHeight(count1: number, count2: number): number {
  // If both widgets have the same number of rows and it's < 10, fit to that count
  const maxCount = Math.max(count1, count2);
  const rows = count1 === count2 && maxCount < 10
    ? maxCount
    : Math.max(maxCount, 10);
  return Math.max(300, Math.min(800, rows * 40 + 80));
}

export function formatPercentage(value: number, isPercentage: boolean = true): string {
  const rounded = Number(value.toFixed(2));
  if (isPercentage) {
    return rounded % 1 === 0 ? `${Math.round(rounded)}%` : `${rounded}%`;
  }
  return rounded % 1 === 0 ? `${Math.round(rounded)}` : `${rounded}`;
}

export function formatInteger(value: number, suffix: string = '%'): string {
  const rounded = Math.round(value);
  return `${rounded}${suffix}`;
}
